import React, { useMemo } from "react";

import Tag from "antd/es/tag";
import groupBy from "lodash-es/groupBy";
import uniqBy from "lodash-es/uniqBy";
import { defineMessages, useIntl } from "react-intl";

import FormLayoutRoleAssociation from "@mapmycustomers/shared/types/layout/FormLayoutRoleAssociation";

import { getRoleDisplayNameAlt } from "@app/util/ui";

import { NO_TEAM_ID } from "../../util/const";

import styles from "./AssociationsList.module.scss";

interface Props {
  associations: Array<FormLayoutRoleAssociation>;
}

const messages = defineMessages({
  noTeam: {
    id: "settings.formLayouts.grid.actions.roles.popover.noTeam",
    defaultMessage: "Users without a team",
    description: "Label for users without a team",
  },
  teamTitle: {
    id: "settings.formLayouts.grid.actions.roles.popover.teamTitle",
    defaultMessage: "{name} ({count} {count, plural, one {Role} other {Roles}})",
    description: "Label of team with roles numbers",
  },
});

const AssociationsList: React.FC<Props> = ({ associations }) => {
  const intl = useIntl();

  const teams = useMemo(
    () =>
      uniqBy(associations, (entry) => entry.team?.id).sort((a, b) => {
        if (b.team === null) {
          return 1;
        }
        if (a.team === null) {
          return -1;
        }

        // Safety check to cover edge case when no team name has been returned - simply ignoring these records
        return a.team?.name?.localeCompare(b.team?.name) ?? 0;
      }),
    [associations]
  );

  const teamRoles = useMemo(
    () => groupBy(associations, (entry) => entry.team?.id ?? NO_TEAM_ID),
    [associations]
  );

  return (
    <div className={styles.container}>
      {teams.map((team) => (
        <div className={styles.team} key={team.team?.id ?? NO_TEAM_ID}>
          {team.team ? (
            <div className={styles.title}>
              {intl.formatMessage(messages.teamTitle, {
                count: teamRoles[team.team.id]?.length ?? 0,
                name: team.team.name,
              })}
            </div>
          ) : (
            <div className={styles.title}>
              {intl.formatMessage(messages.teamTitle, {
                count: teamRoles[NO_TEAM_ID]?.length ?? 0,
                name: intl.formatMessage(messages.noTeam),
              })}
            </div>
          )}
          <div className={styles.roles}>
            {(teamRoles[team.team?.id ?? NO_TEAM_ID] ?? []).map((teamRole) => (
              <Tag key={teamRole.id}>
                {teamRole.role.name ?? getRoleDisplayNameAlt(intl, teamRole.role.key)}
              </Tag>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AssociationsList;
